import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCompanyId } from "@/lib/CompanyContext";
import { useSetupProgress } from "@/lib/useSetupProgress";

/**
 * Mantém o usuário nos "Primeiros Passos" (/comecar, tela cheia sem menu) até
 * TODOS os passos obrigatórios estarem feitos — não basta só o onboarding.
 * Obrigatórios: onboarding, ESCALA, produto e primeira tarefa (integração é
 * opcional). Só então o painel/app "desbloqueia".
 *
 * Lógica:
 *  - Carregando ou sem company → renderiza children (não bloqueia à toa).
 *  - Se ainda faltam passos obrigatórios e a rota não está na allowlist
 *    → redireciona pra /comecar.
 *  - allowlist = as telas usadas PRA COMPLETAR os passos (onboarding/KB/produto,
 *    escala, criar tarefa) + ver as análises que o escala já gera.
 */
const ALLOWED_PATHS_DURING_ONBOARDING = [
  "/briefing", // wizard de onboarding + criação de KB/produto
  "/escala", // diagnóstico
  "/comecar", // a própria trilha
  "/nova-tarefa", // criar a primeira tarefa (passo obrigatório)
  "/resultado", // ver análise das tarefas que o escala dispara
  "/execucao",
];

export function OnboardingGate({ children }: { children: ReactNode }) {
  const companyId = useCompanyId();
  const location = useLocation();
  const progress = useSetupProgress(companyId);

  // Sem company ou ainda carregando o progresso → não bloqueia.
  if (!companyId || progress.loading) {
    return <>{children}</>;
  }

  const currentPath = location.pathname;
  const isAllowed = ALLOWED_PATHS_DURING_ONBOARDING.some((p) => currentPath.startsWith(p));

  if (!progress.allRequiredDone && !isAllowed) {
    return <Navigate to="/comecar" replace />;
  }

  return <>{children}</>;
}
